import { PartitionOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Spin, Table, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { useCachedDataset } from '../../../../api/Numina';
import ToolWrapper from './components/ToolWrapper';

const { Paragraph, Text } = Typography;

const strengthLabels = [
  'Very weak',
  'Weak',
  'Weak–moderate',
  'Moderate',
  'Moderate–strong',
  'Strong',
  'Very strong',
];

const strengthColors = ['default', 'default', 'geekblue', 'blue', 'gold', 'orange', 'red'];
const MIN_ASSOCIATION_LEVEL = 2; // Weak–moderate or above

const formatRate = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${(value * 100).toFixed(digits)}%`;
};

const formatPctValue = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${Number(value).toFixed(digits)}%`;
};

const stripMarkdownBold = (text) => (text ? text.replace(/\*\*/g, '') : text);

function OddsRatioList({ oddsRatios = {}, referenceCategory = '', percTable = {} }) {
  const entries = Object.entries(oddsRatios || {});
  if (!entries.length) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Text strong>Odds ratios</Text>
      <ul style={{ margin: '8px 0', paddingLeft: 18, fontSize: 12 }}>
        {entries
          .filter(([category]) => category !== referenceCategory)
          .sort((a, b) => b[1] - a[1])
          .map(([category, ratio]) => (
            <li key={category}>
              <Text strong>{category}</Text> — {ratio.toFixed(2)}x (
              {formatPctValue((percTable || {})[category])})
            </li>
          ))}
      </ul>
    </div>
  );
}

OddsRatioList.propTypes = {
  oddsRatios: PropTypes.objectOf(PropTypes.number), // { category: ratio }
  referenceCategory: PropTypes.string,
  percTable: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
};

/**
 * Renders segment insights (output of display_segment_insights).
 */
const themeShape = PropTypes.shape({
  theme_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  theme_name: PropTypes.string,
  parent_theme_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parent_theme_name: PropTypes.string,
  is_highlighted: PropTypes.bool,
  is_significant: PropTypes.bool,
  cramers_v_level: PropTypes.number,
  support: PropTypes.number,
  odds_ratios: PropTypes.objectOf(PropTypes.number),
  contingency_table_percentages: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  main_insight: PropTypes.string,
  metric_explanation: PropTypes.string,
  interpretation: PropTypes.string,
  reference_category: PropTypes.string,
});

function DisplaySegmentInsightsTool({ item }) {
  const isPending = item.response === null || item.response === undefined;
  const payload = item.response?.display?.data || item.response?.data || {};
  const status = item.response?.status || 'success';
  const message = item.response?.display?.message || item.response?.message;

  const resultKey = payload.result_key || item.args?.result_key || item.response?.result_key || '';

  const {
    data: cachedData,
    isLoading: fetching,
    error: fetchErrorObj,
  } = useCachedDataset(resultKey, { enabled: !!resultKey });
  const cachedPayload = cachedData?.data || cachedData?.object || cachedData;
  const fetchError = fetchErrorObj ? fetchErrorObj.message : null;

  const segmentInfo = payload.segment_info || cachedPayload?.metadata?.segment_info || {};
  const questionInfo = payload.question_info || cachedPayload?.metadata?.question_info || {};
  const metadata = payload.metadata || cachedPayload?.metadata || {};
  const rawThemes =
    cachedPayload?.full_rows ||
    cachedPayload?.sample_rows ||
    payload.themes ||
    payload.sample_rows ||
    item.response?.sample_rows ||
    item.response?.rows ||
    [];
  const filteredThemes = (rawThemes || []).filter(
    (t) => t && (t.is_highlighted || (t.cramers_v_level || 0) >= MIN_ASSOCIATION_LEVEL),
  );
  const themes = filteredThemes.length ? filteredThemes : rawThemes; // fallback to all so we don't render empty
  const usingFallback = filteredThemes.length === 0;
  const dataSource = payload.data_source || metadata.data_source;
  const hasLlmInsights = metadata.has_llm_insights;

  const columns = [
    {
      title: 'Theme',
      dataIndex: 'theme_name',
      key: 'theme_name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.parent_theme_name && (
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>Group: {record.parent_theme_name}</div>
          )}
          {record.is_highlighted && (
            <Tag color="green" style={{ marginTop: 4, marginLeft: 8 }}>
              Highlighted
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Association',
      dataIndex: 'cramers_v_level',
      key: 'association',
      render: (level) => {
        const label = strengthLabels[level] || 'Unknown';
        const color = strengthColors[level] || 'default';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Support',
      dataIndex: 'support',
      key: 'support',
      render: (value) => formatRate(value),
    },
    {
      title: 'Significant',
      dataIndex: 'is_significant',
      key: 'is_significant',
      render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag>,
    },
  ];

  const expandedRowRender = (record) => {
    const levelLabel = strengthLabels[record.cramers_v_level] || 'Unknown';
    const reference = record.reference_category;
    const cleanInterpretation = stripMarkdownBold(record.interpretation || '');
    const cleanMetric = stripMarkdownBold(record.metric_explanation || '');
    return (
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        <div style={{ marginBottom: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Tag color={strengthColors[record.cramers_v_level] || 'default'}>
            {`Association: ${levelLabel}`}
          </Tag>
          <Tag>{`Support: ${formatRate(record.support)}`}</Tag>
          {reference && <Tag>{`Reference: ${reference}`}</Tag>}
          <Tag color={record.is_highlighted ? 'green' : 'default'}>
            {record.is_highlighted ? 'Highlighted' : 'Not highlighted'}
          </Tag>
        </div>

        {record.main_insight && (
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>Insight: </Text>
            {record.main_insight}
          </Paragraph>
        )}
        {record.metric_explanation && (
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>Stat explanation: </Text>
            {cleanMetric}
          </Paragraph>
        )}
        {record.interpretation && (
          <Paragraph style={{ marginBottom: 8 }}>
            <Text strong>Interpretation: </Text>
            {cleanInterpretation}
          </Paragraph>
        )}

        <OddsRatioList
          oddsRatios={record.odds_ratios}
          referenceCategory={record.reference_category}
          percTable={record.contingency_table_percentages}
        />
      </div>
    );
  };

  if (isPending) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Loading segment insights...</div>
      </div>
    );
  }

  if (status !== 'success' || fetchError) {
    return (
      <Alert
        message="Segment insights failed"
        description={fetchError || message || 'Unexpected error displaying segment insights.'}
        type="error"
        style={{ margin: '8px 0' }}
      />
    );
  }

  if (fetching && (!themes || themes.length === 0)) {
    return (
      <ToolWrapper toolName="Segment Insights" isPending icon={<PartitionOutlined />}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spin size="small" />
          <span style={{ fontSize: 13, color: '#8c8c8c' }}>Loading segment insights...</span>
        </div>
      </ToolWrapper>
    );
  }

  if (!themes || themes.length === 0) {
    return (
      <Alert message="No segment insights to display" type="info" style={{ margin: '8px 0' }} />
    );
  }

  return (
    <ToolWrapper toolName="Segment Insights" isPending={false} icon={<PartitionOutlined />}>
      <Descriptions column={1} bordered size="small" style={{ marginBottom: 12 }}>
        {segmentInfo.segment_text && (
          <Descriptions.Item label="Segment">{segmentInfo.segment_text}</Descriptions.Item>
        )}
        {questionInfo.question_text && (
          <Descriptions.Item label="Question">{questionInfo.question_text}</Descriptions.Item>
        )}
        <Descriptions.Item label="Source">
          <Tag color={dataSource === 'pre_calculated' ? 'blue' : 'gold'}>
            {dataSource || 'unknown'}
          </Tag>
          {hasLlmInsights && <Tag color="green">LLM insights</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Themes analyzed">
          {metadata.total_themes_analyzed || themes.length}
        </Descriptions.Item>
        {metadata.timestamp && (
          <Descriptions.Item label="Timestamp">{metadata.timestamp}</Descriptions.Item>
        )}
        {usingFallback && (
          <Descriptions.Item label="Notice">
            No themes met the highlight threshold; showing all available themes.
          </Descriptions.Item>
        )}
      </Descriptions>

      <Table
        dataSource={themes}
        columns={columns}
        rowKey={(record) => record.theme_id || record.theme_name}
        size="small"
        pagination={{ pageSize: 8 }}
        expandable={{ expandedRowRender }}
      />
    </ToolWrapper>
  );
}

DisplaySegmentInsightsTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      result_key: PropTypes.string,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      message: PropTypes.string,
      result_key: PropTypes.string,
      display: PropTypes.shape({
        result_key: PropTypes.string,
        message: PropTypes.string,
        data: PropTypes.shape({
          result_key: PropTypes.string,
          segment_info: PropTypes.shape({
            segment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            segment_text: PropTypes.string,
          }),
          question_info: PropTypes.shape({
            question_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            question_text: PropTypes.string,
          }),
          themes: PropTypes.arrayOf(themeShape),
          sample_rows: PropTypes.arrayOf(themeShape),
          rows: PropTypes.arrayOf(themeShape),
          metadata: PropTypes.shape({
            total_themes_analyzed: PropTypes.number,
            timestamp: PropTypes.string,
            data_source: PropTypes.string,
            has_llm_insights: PropTypes.bool,
          }),
          data_source: PropTypes.string,
        }),
      }),
      sample_rows: PropTypes.arrayOf(themeShape),
      rows: PropTypes.arrayOf(themeShape),
      data: PropTypes.shape({
        themes: PropTypes.arrayOf(themeShape),
        sample_rows: PropTypes.arrayOf(themeShape),
        rows: PropTypes.arrayOf(themeShape),
      }),
    }),
  }).isRequired,
};

export default DisplaySegmentInsightsTool;
